import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/services/session';
import { Logo } from '@/design-system/Logo';
import { Button } from '@/design-system/Button';
import { Card, Chip, SectionHeading } from '@/design-system/Surfaces';
import { SparkleField, SparkleGlyph } from '@/design-system/Sparkle';
import { cn } from '@/design-system/cn';
import { IconBook, IconGrid, IconHome, IconLock, IconPalette } from '@/features/studio/Icons';
import { ChildGate, useChild } from './childContext';

export const AVATARS = [
  { id: 'fox', label: 'Fox', emoji: '🦊', tint: 'bg-sunny/30' },
  { id: 'moth', label: 'Moth', emoji: '🦋', tint: 'bg-accent-soft' },
  { id: 'owl', label: 'Owl', emoji: '🦉', tint: 'bg-primary-soft' },
  { id: 'hedgehog', label: 'Hedgehog', emoji: '🦔', tint: 'bg-secondary-soft' },
  { id: 'frog', label: 'Frog', emoji: '🐸', tint: 'bg-leaf/20' },
  { id: 'star', label: 'Star', emoji: '⭐️', tint: 'bg-sunny/30' },
  { id: 'moon', label: 'Moon', emoji: '🌙', tint: 'bg-primary-soft' },
  { id: 'mushroom', label: 'Mushroom', emoji: '🍄', tint: 'bg-secondary-soft' },
];

export function avatarFor(id: string) {
  return AVATARS.find((a) => a.id === id) ?? AVATARS[0];
}

/**
 * The family app shell (spec §2.2).
 *
 * Its entry point is the profile chooser, never the marketing homepage —
 * which is also what the native app opens to (spec §2.2, §17.1).
 */
export function FamilyLayout() {
  const load = useSession((s) => s.load);
  const loaded = useSession((s) => s.loaded);
  useEffect(() => {
    void load();
  }, [load]);

  if (!loaded) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-paper">
        <SparkleGlyph className="h-10 w-10 animate-twinkle text-magic" />
      </div>
    );
  }
  return <Outlet />;
}

/* ------------------------------------------------------------------ *
 * A01 — Welcome / profile chooser
 * ------------------------------------------------------------------ */

export function AppWelcome() {
  const children = useSession((s) => s.children);
  const setActive = useSession((s) => s.setActiveChild);
  const navigate = useNavigate();

  if (children.length === 0) {
    return (
      <Shell>
        <div className="mx-auto max-w-lg text-center">
          <Logo size="lg" />
          <p className="mt-l text-[18px] leading-relaxed text-ink-soft">
            Set up a space for your child. It takes a minute, and asks for as little as possible.
          </p>
          <div className="mt-2xl flex flex-col gap-m">
            <Button variant="magic" size="xl" to="/app/setup">
              Set up a family
            </Button>
            <Button variant="outline" size="lg" to="/try">
              Try a story first
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <SectionHeading align="center" eyebrow="Welcome back" title="Who is reading?" />
      <div className="mx-auto mt-2xl grid max-w-3xl grid-cols-2 gap-l sm:grid-cols-3">
        {children.map((c) => {
          const a = avatarFor(c.avatar);
          return (
            <button
              key={c.id}
              onClick={() => {
                setActive(c.id);
                navigate(`/app/child/${c.id}/home`);
              }}
              className="rounded-blob border-[3px] border-line bg-card p-l shadow-cut transition hover:-translate-y-1.5 hover:shadow-cut-lg"
            >
              <span className={cn('mx-auto grid h-24 w-24 place-items-center rounded-full text-[46px]', a.tint)}>
                <span aria-hidden>{a.emoji}</span>
              </span>
              <span className="mt-m block font-display text-[19px] font-semibold text-ink">{c.nickname}</span>
            </button>
          );
        })}
        <Link
          to="/app/setup"
          className="grid place-items-center rounded-blob border-[3px] border-dashed border-line p-l text-center font-display text-[16px] font-semibold text-ink-soft"
        >
          <span>
            <span aria-hidden className="block text-[36px] text-magic">+</span>
            Add a child
          </span>
        </Link>
      </div>

      <div className="mt-3xl text-center">
        <Button variant="ghost" size="md" to="/app/parent" icon={<IconLock />}>
          Grown-ups
        </Button>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative min-h-[100dvh] bg-paper">
      <div aria-hidden className="absolute inset-x-0 top-0 h-[360px]" style={{ background: 'var(--gradient-dawn)' }} />
      <SparkleField count={10} seed={23} className="h-[360px]" />
      <div className="relative u-shell py-3xl">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * A03/A05 — Setup: notices, then a child profile
 * ------------------------------------------------------------------ */

export function AppSetup() {
  const addChild = useSession((s) => s.addChild);
  const setActive = useSession((s) => s.setActiveChild);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState(AVATARS[0].id);
  const [ageBand, setAgeBand] = useState<'3-4' | '5-7'>('3-4');
  const [mode, setMode] = useState<'listen-together' | 'read-with-help' | 'read-myself'>('listen-together');
  const [interests, setInterests] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const INTERESTS = ['Animals', 'Nature', 'Space', 'Making things', 'Night-time', 'Being brave'];

  const create = async () => {
    setBusy(true);
    const child = await addChild({
      nickname: nickname.trim() || 'Reader',
      avatar,
      ageBand,
      readingMode: mode,
      interests,
      // Off by default; a parent turns it on (spec §8.4).
      bedtime: false,
    });
    setActive(child.id);
    navigate(`/app/child/${child.id}/home`);
  };

  return (
    <Shell>
      <div className="mx-auto max-w-xl">
        <p className="mb-l text-center font-display text-[14px] font-semibold uppercase tracking-wide text-ink-faint">
          Step {step + 1} of 3
        </p>

        {step === 0 ? (
          <Card>
            <h1 className="font-display text-[26px] font-semibold text-ink">Before we start</h1>
            <div className="mt-l space-y-m text-[16px] leading-relaxed text-ink-soft">
              <p>
                This is a grown-up's account. Each child gets a private space inside it. Nothing a
                child makes is ever public unless you approve that exact picture.
              </p>
              <p>
                Right now everything is stored on this device only. There is no account server, so
                nothing is sent anywhere.
              </p>
              <div className="rounded-pebble border-2 border-dashed border-line bg-sunny/15 p-l text-ink">
                <strong className="font-display">Not yet built:</strong> verified parental consent,
                cloud sync, and a parent access code that a backend can actually enforce. Those are
                required before this collects real child data, and the app will not pretend
                otherwise in the meantime.
              </div>
            </div>
            <Button variant="magic" size="lg" className="mt-xl" block onClick={() => setStep(1)}>
              I understand — continue
            </Button>
          </Card>
        ) : null}

        {step === 1 ? (
          <Card>
            <h1 className="font-display text-[26px] font-semibold text-ink">Set up your child</h1>
            <p className="mt-s text-[16px] text-ink-soft">
              A nickname is enough. Please do not use their full name.
            </p>

            <label className="mt-xl block font-display text-[15px] font-semibold text-ink" htmlFor="nickname">
              What should we call them?
            </label>
            <input
              id="nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={20}
              placeholder="Bean"
              className="mt-s min-h-touch-lg w-full rounded-pebble border-2 border-line bg-paper px-l font-display text-[18px] text-ink"
            />

            <fieldset className="mt-xl">
              <legend className="font-display text-[15px] font-semibold text-ink">Pick a picture</legend>
              <div className="mt-m grid grid-cols-4 gap-m">
                {AVATARS.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setAvatar(a.id)}
                    aria-pressed={avatar === a.id}
                    aria-label={a.label}
                    className={cn(
                      'grid aspect-square place-items-center rounded-blob border-[3px] text-[32px] transition',
                      a.tint,
                      avatar === a.id ? 'border-magic scale-105 shadow-cut' : 'border-line',
                    )}
                  >
                    <span aria-hidden>{a.emoji}</span>
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-xl">
              <legend className="font-display text-[15px] font-semibold text-ink">Roughly how old?</legend>
              <p className="mt-xs text-[14px] text-ink-faint">A broad band only — we never ask for a birth date.</p>
              <div className="mt-m flex gap-m">
                {(['3-4', '5-7'] as const).map((b) => (
                  <button
                    key={b}
                    onClick={() => setAgeBand(b)}
                    aria-pressed={ageBand === b}
                    className={cn(
                      'min-h-touch-lg flex-1 rounded-pebble border-2 font-display text-[17px] font-semibold',
                      ageBand === b ? 'border-primary bg-primary-soft text-primary-deep' : 'border-line bg-card text-ink-soft',
                    )}
                  >
                    {b === '3-4' ? '3 to 4' : '5 to 7'}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-xl">
              <legend className="font-display text-[15px] font-semibold text-ink">How do they read best?</legend>
              <p className="mt-xs text-[14px] text-ink-faint">You can change this any time, mid-story.</p>
              <div className="mt-m grid gap-s">
                {([
                  ['listen-together', 'Listen together', 'The story reads itself while you sit with them.'],
                  ['read-with-help', 'Read with help', 'They read; a tap hears a page again.'],
                  ['read-myself', 'Read myself', 'Narration off, help still one tap away.'],
                ] as const).map(([id, label, desc]) => (
                  <button
                    key={id}
                    onClick={() => setMode(id)}
                    aria-pressed={mode === id}
                    className={cn(
                      'rounded-pebble border-2 p-l text-left',
                      mode === id ? 'border-primary bg-primary-soft' : 'border-line bg-card',
                    )}
                  >
                    <span className="block font-display text-[17px] font-semibold text-ink">{label}</span>
                    <span className="mt-xs block text-[15px] text-ink-soft">{desc}</span>
                  </button>
                ))}
              </div>
            </fieldset>

            <Button variant="magic" size="lg" className="mt-xl" block onClick={() => setStep(2)}>
              Continue
            </Button>
          </Card>
        ) : null}

        {step === 2 ? (
          <Card>
            <h1 className="font-display text-[26px] font-semibold text-ink">Anything they love?</h1>
            <p className="mt-s text-[16px] text-ink-soft">Optional — it only nudges which stories we show first.</p>
            <div className="mt-xl flex flex-wrap gap-s">
              {INTERESTS.map((i) => {
                const on = interests.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() => setInterests((p) => (on ? p.filter((x) => x !== i) : [...p, i]))}
                    aria-pressed={on}
                    className={cn(
                      'min-h-touch rounded-full border-2 px-l font-display text-[16px] font-semibold',
                      on ? 'border-accent bg-accent-soft text-accent' : 'border-line bg-card text-ink-soft',
                    )}
                  >
                    {i}
                  </button>
                );
              })}
            </div>
            <Button variant="magic" size="lg" className="mt-2xl" block loading={busy} onClick={create}>
              Take me to the stories
            </Button>
            <button
              onClick={create}
              className="mt-m w-full text-center font-display text-[15px] font-semibold text-ink-faint underline underline-offset-4"
            >
              Skip
            </button>
          </Card>
        ) : null}
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------------------ *
 * Child navigation shell — Home, Books, Create, Gallery (spec §3)
 * ------------------------------------------------------------------ */

export function ChildShell() {
  return (
    <ChildGate>
      <ChildTabs />
    </ChildGate>
  );
}

function ChildTabs() {
  const child = useChild();
  const { pathname } = useLocation();

  const tabs = [
    { to: `/app/child/${child.id}/home`, label: 'Home', icon: <IconHome /> },
    { to: `/app/child/${child.id}/books`, label: 'Books', icon: <IconBook /> },
    { to: `/app/child/${child.id}/create`, label: 'Create', icon: <IconPalette /> },
    { to: `/app/child/${child.id}/my-art`, label: 'My Art', icon: <IconGrid /> },
  ];

  return (
    <div className="flex min-h-[100dvh] flex-col bg-paper">
      <main className="flex-1 pb-[104px]">
        <Outlet />
      </main>
      <nav
        aria-label="Sections"
        className="fixed inset-x-0 bottom-0 z-30 border-t-2 border-line bg-card px-s pb-[max(8px,env(safe-area-inset-bottom))] pt-s"
      >
        <ul className="mx-auto flex max-w-lg">
          {tabs.map((t) => (
            <li key={t.to} className="flex-1">
              <Link
                to={t.to}
                className={cn(
                  'flex min-h-touch-lg flex-col items-center justify-center gap-[2px] rounded-pebble font-display text-[12px] font-semibold',
                  pathname === t.to ? 'bg-primary-soft text-primary-deep' : 'text-ink-soft',
                )}
              >
                <span aria-hidden>{t.icon}</span>
                {t.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

export { Chip };
